import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from "react";
import Emittery from "emittery";

interface IContextProps {
  subscribe: (
    type: EVENT_TYPE,
    callback: (data?: any) => void | Promise<void>,
  ) => void | Promise<void>;
  unsubscribe: (
    type: EVENT_TYPE,
    callback: (data?: any) => void | Promise<void>,
  ) => void;
  publish: (type: EVENT_TYPE, data?: any) => void;
}

const PubSubContext = createContext<IContextProps>({
  subscribe: () => {},
  unsubscribe: () => {},
  publish: () => {},
});

const emitter = new Emittery();

export type EVENT_TYPE =
  | "ws-reconnect"
  | "draft-message-sent"
  | "message-seen"
  | "message-in-view"
  | "message-not-in-view"
  | "new-message"
  | "message-thread-translated"
  | "message-updated"
  | "focus-quill-editor";

export const PubSubProvider = ({ children }: PropsWithChildren) => {
  const subscribe = useCallback(
    (type: EVENT_TYPE, callback: (data?: any) => void | Promise<void>) => {
      emitter.on(type, callback);
    },
    [],
  );

  const unsubscribe = useCallback(
    (type: EVENT_TYPE, callback: (data?: any) => void | Promise<void>) => {
      emitter.off(type, callback);
    },
    [],
  );

  const publish = useCallback((type: EVENT_TYPE, data?: any) => {
    emitter.emit(type, data);
  }, []);

  const value = useMemo<IContextProps>(
    () => ({
      subscribe,
      unsubscribe,
      publish,
    }),
    [subscribe, unsubscribe, publish],
  );

  return (
    <PubSubContext.Provider value={value}>{children}</PubSubContext.Provider>
  );
};

export const usePubSub = () => useContext(PubSubContext);
